<!-- src/views/OperatorsView.html -->
<div class="view-container">
    <div class="header-section">
      <div>
        <h1 class="view-title">Gestione Collaboratori</h1>
        <p class="view-desc">In questa sezione potrai definire l'anagrafica, le qualifiche e l'organizzazione dei turni degli operatori.</p>
      </div>
      <button v-if="activeTab === 'operators'" class="btn-primary rounded-soft" @click="openOpCreate">
        <Plus :size="18" />
        Nuovo Operatore
      </button>
    </div>

    <!-- Tabs Navigation -->
    <div class="tabs-nav border-bottom">
      <button 
        class="tab-btn" 
        :class="{ 'active': activeTab === 'operators' }"
        @click="activeTab = 'operators'"
      >
        <Users :size="18" />
        Elenco Operatori
      </button>
      <button 
        class="tab-btn" 
        :class="{ 'active': activeTab === 'schedules' }"
        @click="activeTab = 'schedules'"
      >
        <CalendarClock :size="18" />
        Orari Operatori
      </button>
    </div>

    <!-- TAB: OPERATORS -->
    <div v-if="activeTab === 'operators'" class="tab-content">
      <div class="operators-grid">
        <div v-for="op in operators" :key="op.id" class="operator-card rounded-soft shadow-soft">
          <div class="op-card-header">
            <div class="op-avatar">
              <img :src="op.avatar" alt="Avatar" />
              <div class="status-indicator" :class="op.isActive ? 'bg-success' : 'bg-muted'"></div>
            </div>
            <div class="op-actions">
              <button class="btn-icon rounded-soft btn-edit" @click="openOpEdit(op)" title="Modifica">
                <Edit :size="16" />
              </button>
              <button class="btn-icon rounded-soft btn-delete" @click="deleteOperator(op.id)" title="Cancella">
                <Trash2 :size="16" />
              </button>
            </div>
          </div>
          <div class="op-card-body">
            <h3 class="op-name">{{ op.name }}</h3>
            <p class="op-role text-muted">{{ op.qualifica }}</p>
            <p class="op-bio text-light line-clamp-2" :title="op.bio">{{ op.bio || 'Nessuna bio inserita.' }}</p>
            <div class="op-skills mt-3">
              <span v-for="skillId in op.skills" :key="skillId" class="skill-chip read-only">
                {{ getSkillName(skillId) }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="operators.length === 0" class="empty-state w-100">
          Nessun operatore configurato nell'anagrafica.
        </div>
      </div>
    </div>

    <!-- TAB: SCHEDULES -->
    <div v-if="activeTab === 'schedules'" class="tab-content">
      <div class="table-container rounded-soft shadow-soft">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 25%">Operatore</th>
              <th>Resoconto Turni (Settimana)</th>
              <th style="text-align: right; width: 100px;">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="schedules.length === 0">
              <td colspan="3" class="empty-state">Nessun calendario disponibile.</td>
            </tr>
            <tr v-for="sched in schedules" :key="sched.operatorId">
              <td class="font-medium text-light">
                <div class="sched-op-info">
                  <img :src="getOperator(sched.operatorId)?.avatar" class="mini-avatar" alt=""/>
                  {{ getOperator(sched.operatorId)?.name }}
                </div>
              </td>
              <td>
                <div class="sched-badges">
                  <span v-for="shift in sched.shifts" :key="shift.day" 
                        class="shift-mini-badge" 
                        :class="shift.isWorking ? 'working' : 'rest'"
                        :title="shift.isWorking ? `${shift.start} - ${shift.end}` : 'Riposo'">
                    {{ shift.day.substring(0, 3) }}: {{ shift.isWorking ? `${shift.start} - ${shift.end}` : 'Off' }}
                  </span>
                </div>
              </td>
              <td class="actions-cell">
                <button class="btn-icon rounded-soft btn-edit" @click="openSchedEdit(sched.operatorId)" title="Modifica Turni">
                  <Edit :size="16" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- OVERLAYS E DRAWERS -->
    
    <!-- Drawer Operators -->
    <div 
      class="drawer-overlay" 
      :class="{ 'is-visible': isOpDrawerOpen }"
      @click="closeOpDrawer"
    ></div>
    <div 
      class="drawer drawer-wide"
      :class="{ 'is-open': isOpDrawerOpen }"
    >
      <div class="drawer-header border-bottom">
        <h2>{{ opForm.id === 0 ? 'Nuovo Operatore' : 'Modifica Operatore' }}</h2>
        <button class="btn-close" @click="closeOpDrawer">
          <X :size="20" />
        </button>
      </div>
      <div class="drawer-body">
        <form @submit.prevent="saveOperator" id="op-form">
          <div class="avatar-upload-section mb-4">
             <div class="avatar-preview">
                <img v-if="opForm.avatar" :src="opForm.avatar" alt="Avatar Preview" />
                <div v-else class="avatar-placeholder"><Camera :size="24" /></div>
             </div>
             <div class="avatar-controls">
                <label>URL Immagine Profilo</label>
                <input type="text" v-model="opForm.avatar" placeholder="Lascia vuoto per avatar testuale..." />
                <span class="helper-text">Inseriscine una se la possiedi (URL pubblico).</span>
             </div>
          </div>

          <div class="form-row">
            <div class="form-group half-width">
              <label for="name">Nome Operatore *</label>
              <input type="text" id="name" v-model="opForm.name" required />
            </div>
            <div class="form-group half-width">
              <label for="role">Qualifica *</label>
              <input type="text" id="role" v-model="opForm.qualifica" required />
            </div>
          </div>

          <div class="form-group">
            <label for="bio">Biografia</label>
            <textarea id="bio" v-model="opForm.bio" rows="3" placeholder="Scrivi qualche riga di presentazione..."></textarea>
          </div>

          <div class="form-group" style="margin-bottom: 0.5rem;"> 
            <label>Competenze (Skills)</label>
            <div class="skills-input-row">
              <select v-model="selectedSkillToAdd" class="form-control">
                <option value="" disabled>Seleziona una competenza</option>
                <option v-for="skill in availableSkills" :key="skill.id" :value="skill">
                  {{ skill.name }}
                </option> 
              </select>
              
              <button type="button" class="btn-primary" @click="addSkill" :disabled="!selectedSkillToAdd">
                Aggiungi
              </button>
            </div>
            
            <div v-if="opForm.skills.length > 0" class="op-skills" style="margin-top: 8px;">
              <span v-for="skill in opForm.skills" :key="skill.id" class="skill-chip">
                {{ skill.name }}
                <button type="button" class="chip-remove" @click="removeSkill(skill)">x</button>
              </span>
            </div>
          </div>

          <div class="form-group toggle-group mt-3">
            <label class="toggle-label text-light">Stato Operatore (Attivo / Inattivo)</label>
            <label class="switch">
              <input type="checkbox" v-model="opForm.isActive">
              <span class="slider round"></span>
            </label>
          </div>

          <div v-if="opFormError" class="alert-error mt-3">
            <AlertCircle :size="16" />
            <span>{{ opFormError }}</span>
          </div>
        </form>
      </div>
      <div class="drawer-footer border-top">
        <button type="button" class="btn-secondary rounded-soft" @click="closeOpDrawer">Annulla</button>
        <button type="submit" form="op-form" class="btn-primary rounded-soft">Salva Operatore</button>
      </div>
    </div>

    <!-- Drawer Schedules -->
    <div 
      class="drawer-overlay" 
      :class="{ 'is-visible': isSchedDrawerOpen }"
      @click="closeSchedDrawer"
    ></div>
    <div 
      class="drawer drawer-wide"
      :class="{ 'is-open': isSchedDrawerOpen }"
    >
      <div class="drawer-header border-bottom">
        <h2>Turni - {{ currentEditingSchedOp?.name }}</h2>
        <button class="btn-close" @click="closeSchedDrawer">
          <X :size="20" />
        </button>
      </div>
      <div class="drawer-body">
        <form @submit.prevent="saveSchedule" id="sched-form">
          <p class="text-muted mb-4">Seleziona per ciascun giorno della settimana le ore di servizio.</p>
          
          <div class="shift-grid">
            <div v-for="shift in editingShifts" :key="shift.day" class="shift-row rounded-soft border">
              <div class="shift-day-col font-medium text-light">
                {{ shift.day }}
              </div>
              <div class="shift-toggle-col">
                 <label class="switch switch-sm">
                    <input type="checkbox" v-model="shift.isWorking">
                    <span class="slider round"></span>
                  </label>
                  <span class="text-muted ms-2">{{ shift.isWorking ? 'Lavora' : 'Riposo' }}</span>
              </div>
              <div class="shift-time-col" :class="{ 'opacity-50 pointer-events-none': !shift.isWorking }">
                 <input type="time" v-model="shift.start" class="shift-time-input" :disabled="!shift.isWorking"/>
                 <span class="text-muted">-</span>
                 <input type="time" v-model="shift.end" class="shift-time-input" :disabled="!shift.isWorking"/>
              </div>
            </div>
          </div>

          <div v-if="schedError" class="alert-error mt-3">
            <AlertCircle :size="16" />
            <span>{{ schedError }}</span>
          </div>
        </form>
      </div>
      <div class="drawer-footer border-top">
        <button type="button" class="btn-secondary rounded-soft" @click="closeSchedDrawer">Annulla</button>
        <button type="submit" form="sched-form" class="btn-primary rounded-soft">Salva Turni</button>
      </div>
    </div>


  </div>
